import { AlertTriangle, ArrowRight, Calendar, Building2, MessageSquare, UserX } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent, Button, Skeleton } from '@/components/ui'
import { useTasks } from '@/hooks'
import { useUiStore } from '@/stores/uiStore'
import { useStreamChat } from '@/hooks'
import { cn, daysUntil } from '@/lib/utils'
import type { Task } from '@/api'

function attentionScore(task: Task): number {
  // Overdue tasks first, then unassigned, then due soon
  const days = task.due_date ? daysUntil(task.due_date) : 30
  if (days < 0) return 0 + days // more overdue = higher priority
  if (!task.assignee_id && days <= 7) return 100 + days // unassigned + approaching
  if (days === 0) return 200
  if (days <= 3) return 300 + days
  return 400 + days
}

function getAttentionBorder(task: Task): string {
  if (!task.due_date) return 'border-l-muted-foreground'
  const days = daysUntil(task.due_date)
  if (days < 0) return 'border-l-red-500'
  if (!task.assignee_id) return 'border-l-amber-500'
  if (days <= 3) return 'border-l-amber-500'
  return 'border-l-muted-foreground'
}

export function NeedsAttention() {
  const { data, isLoading } = useTasks({ per_page: 50 })
  const { setChatOpen } = useUiStore()
  const { send } = useStreamChat()

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Needs Attention
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-[60px] rounded-lg" />
          ))}
        </CardContent>
      </Card>
    )
  }

  // Filter to active tasks that need attention: overdue, unassigned, or due soon
  const attentionTasks = (data?.items ?? [])
    .filter((t) => {
      if (t.status === 'completed') return false
      if (!t.due_date) return !t.assignee_id // unassigned with no date still matters
      const days = daysUntil(t.due_date)
      return days < 0 || !t.assignee_id || days <= 7
    })
    .sort((a, b) => attentionScore(a) - attentionScore(b))
    .slice(0, 7)

  const handleAskCopilot = (task: Task) => {
    setChatOpen(true)
    if (!task.assignee_id) {
      send(`Who should I assign "${task.title}" to?`)
    } else {
      send(`What's the status on "${task.title}"? It needs attention.`)
    }
  }

  if (attentionTasks.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Needs Attention
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-6">
            Nothing urgent — the team's on track
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Needs Attention
        </CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/tasks">
            View all <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {attentionTasks.map((task) => {
          const days = task.due_date ? daysUntil(task.due_date) : null
          const isOverdue = days !== null && days < 0
          const isUnassigned = !task.assignee_id

          return (
            <div
              key={task.id}
              className={cn(
                'flex items-center justify-between p-3 rounded-lg border-l-4 bg-muted/20',
                'hover:bg-muted/40 transition-colors',
                getAttentionBorder(task),
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{task.title}</p>
                  {isOverdue && (
                    <span className="flex-shrink-0 text-[10px] font-semibold font-mono px-1.5 py-0.5 rounded bg-red-500/10 text-red-600 dark:text-red-400">
                      OVERDUE
                    </span>
                  )}
                  {isUnassigned && (
                    <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400">
                      UNASSIGNED
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  {task.client_name && (
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {task.client_name}
                    </span>
                  )}
                  {task.assignee_name ? (
                    <span className="flex items-center gap-1">
                      Assigned: {task.assignee_name}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                      <UserX className="h-3 w-3" />
                      No one assigned
                    </span>
                  )}
                  {task.due_date && (
                    <span
                      className={cn(
                        'flex items-center gap-1',
                        isOverdue && 'text-red-600 dark:text-red-400',
                      )}
                    >
                      <Calendar className="h-3 w-3" />
                      {isOverdue
                        ? `Overdue ${Math.abs(days!)}d`
                        : days === 0
                        ? 'Due today'
                        : `Due in ${days}d`}
                    </span>
                  )}
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0 ml-2 hover:text-primary"
                title="Ask Copilot"
                onClick={() => handleAskCopilot(task)}
              >
                <MessageSquare className="h-3.5 w-3.5" />
              </Button>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
